/* 
 * This file is created by Vatsaly Patel
 * Note: If you are editing anything, please let me know - or do comment [it's compulsory]
*/

import React, { useRef, useCallback, useState, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { useHistory } from "react-router-dom";
import appRoutes from "../../routes/appRoutes";
import { CircularProgress } from "@material-ui/core";
import { getTicketsList } from "../../apiRoutes";
import moment from "moment-timezone";
import Label from "../../components/label";

function createData(
  _id,
  patientUIN,
  doctorUIN,
  patientName,
  doctorName,
  dateResolved,
  caseNumber,
  dateReceived,
  patientId,
  subject,
  responsible,
  status,
  onHoldDate
) {
  return {
    _id,
    patientUIN,
    doctorUIN,
    patientName,
    doctorName,
    dateResolved,
    caseNumber,
    dateReceived,
    patientId,
    subject,
    responsible,
    status,
    onHoldDate
  };
}

export default function StickyHeadTable(props) {
  const history = useHistory(); // go to other page with or without data
  const observer = useRef(); // to check it is the last element or not - if it is last element then call an API
  const [pageNo, setPageNumber] = useState(1); // just set page number if needed in future. 
  const [nextPage, setNextPage] = useState(false); // set next page if there is more data 
  const [loading, setLoading] = useState(true); // loading variable used to reduce multiple API calls
  const [progressBar, setProgressBar] = useState(false); // progress bar hide and show
  const [limit, setLimit] = useState(10); // limit is fixed but if we need to change in future then change it here
  const [skip, setSkip] = useState(0); // set Skip page
  const [noMoreData, setNoMoreData] = useState(false); // no data found block hide-show
  const [startDate, setStartDate] = useState(moment().subtract(1, "month").startOf("day"));
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  // Column Names - if you want to add an column then add id and label here - also take care of createData function (need to pass data)
  const columns = [
    {
      id: "caseNumber",
      label: "Ticket Number"
    },
    {
      id: "dateReceived",
      label: "Date Received"
    },
    {
      id: "patientId",
      label: "Patient"
    },
    {
      id: "subject",
      label: "Subject"
    },
    {
      id: "responsible",
      label: "Responsible"
    },
    {
      id: "status",
      label: "Status"
    }
  ];

   // Data to be appended
   const [rows, setRows] = useState([]); // setting rows
   const [appendRows, setAppendRows] = useState([]); // not used for now (if needed in future)

   /*
  * Go to edit ticket page - need to pass _id to get the data on page
  */
  const gotToEditTicket = (event, no) => {
    history.push(appRoutes.editTicket + "/" + no, { _id: no });
  };

  /*
   * Check last element is visible or not on scroll
  */
  const lastElementRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(data => {
      if (data) {
        if (data[0].isIntersecting && nextPage && loading) {
          setLoading(false);
          if (loading) {
            let skipCalculate = limit * pageNo,
                currentPageNo = pageNo + 1;
            setPageNumber(currentPageNo);
            setSkip(skipCalculate);
            getTickets(limit, skipCalculate, props.statusFilter, currentPageNo);
          }
          setTimeout(() => {
            setLoading(true);
          }, 2000);
        }
      }
    });
    if (node) observer.current.observe(node);
  });

  /*
  * Main function to call an API
  * Pass parameters compulusry (limit, skipPage, filterStatus) to calculate pagination and check filter
  */

  const getTickets = useCallback((limitPage, skipPage, filterStatus, currentPage) => {
    let updatedParam = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      limit: limitPage,
      skip: skipPage
    };
    if(filterStatus !== "") updatedParam['status'] = filterStatus;
    let apiCallParam = {
      reqType: "GET",
      reqURL: getTicketsList,
      dataObj: {},
      params: updatedParam,
      headers: {},
      successCallback: res => {
        // calculate is there any next page or not ?        
        let currentRowCount = res.data.data.count - (currentPage * limitPage);
        if (currentRowCount > 0) {
          setNextPage(true);
        } else {
          setNextPage(false);
          setNoMoreData(true);
          setProgressBar(false);
        }

        // set the data
        let ticketData = [];
        res.data.data.tickets.map((value, index) => {          
          let status = "";
          if (value.status === "IN_PROGRESS") {
            status = "In Progress";
          } else if (value.status === "OPEN") {
            status = "Unopened";
          } else if (value.status === "ON_HOLD") {
            status = "On Hold";
          } else if (value.status === "RESOLVED") {
            status = "Resolved";
          } else if (value.status === "DOCTOR_ACTION_REQUIRED") {
            status = "Doctor Action Required";
          } else if (value.status === "ADMIN_ACTION_REQUIRED") {
            status = "Support Action Required";
          } else {
            status = "Reopened";
          }

          let responsiblePersonName = '-';
          if(value.responsible && value.responsible.firstName && value.responsible.lastName) {
            if(value.responsible.firstName === "System" || value.responsible.lastName === "System") {
              responsiblePersonName = 'Instant Consult'
            } else if(value.responsible && value.responsible.firstName && value.responsible.lastName) {
              responsiblePersonName = `${value.responsible.firstName} ${value.responsible.lastName}`
            }
          }
         
          ticketData.push(
            // createData used for Material UI table to set the data (if you include anything, don't forget to include in createData function at the top or bottom of the file)
            createData(
              value._id,
              value.patientId ? value.patientId.patientId : '-',
              value.doctorId ? value.doctorId.doctorId : '-',
              (value.patientId && value.patientId.firstName ? value.patientId.firstName + " " + value.patientId.lastName : '-'),
              (value.doctorId && value.doctorId.firstName ? value.doctorId.firstName + " " + value.doctorId.lastName : '-'),
              (value.dateResolved ? moment(value.dateResolved).tz("Australia/Brisbane").format("DD-MM-YYYY"): "-"),
              value.caseNumber ? value.caseNumber : '-',
              value.dateReceived ? moment(value.dateReceived).tz("Australia/Brisbane").format("DD-MM-YYYY"): "-",
              value.patientId ? ("UIN: " + value.patientId.patientId + " | " + value.patientId.firstName + " " + value.patientId.lastName) : '-',
              value.subject ? value.subject : '-',
              responsiblePersonName,
              status ? status : '-',
              value.onHoldDate ? value.onHoldDate : null
            )
          );
        });        
        if (skipPage === 0) {
          setRows(ticketData);
        } else {
          setRows(prev => prev.concat(ticketData));
        }
      },
      failureCallback: err => {
        console.log("Something went wrong. Please try again ", err);
           setNextPage(false);
          setNoMoreData(true);
          setProgressBar(false);
      },
      showPopUp: false
    };
    props.makeAPICall(apiCallParam);
  });
  
  /*
   * Watch on filter
  */
  useEffect(() => {
    resetPagination();
    setRows([]);
    getTickets(limit, 0, props.statusFilter, 1);
  }, [props.statusFilter]);

  /*
   * Watch on rows
  */
  useEffect(() => {
    props.updateTicketData(rows);
  }, [rows]);

  /*
  * Reset pagination
  */
  const resetPagination = () => {
    setPageNumber(1);
    setNextPage(false);
    setLoading(true);
    setProgressBar(true);
    setSkip(0);
    setNoMoreData(false);
  };

  return (
    <Paper className="w-100 ticket-list-tbl-div">
      <TableContainer className="table-max-height primary-table-color">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            {/* Loop on Column name  */}
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loop over the data (createData function) */}
            {rows.map((row, index) => {
              /*
              * If it is last element then take the referent to call an API Else print another block
              */
              if (rows.length === index + 1) {
                return (
                  <TableRow
                    className="cursor-pointer"
                    ref={lastElementRef}
                    hover
                    key={row._id}
                    onClick={event => gotToEditTicket(event, row._id)}
                  >
                    {columns.map(column => {
                      const value = row[column.id];
                      return (
                        <TableCell 
                          className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "border-bottom-0"}
                          key={column.id} align={column.align}>
                          {column.format && typeof value === "number"
                            ? column.format(value)
                            : value}
                          {column.id === "status" && row.onHoldDate ? <p>{moment(row.onHoldDate).format("DD-MM-YYYY")}</p>  : ''}

                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              } else {
                return (
                  <TableRow
                    className="cursor-pointer"
                    hover
                    key={row._id}
                    onClick={event => gotToEditTicket(event, row._id)}
                  >
                    {columns.map(column => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === "number"
                            ? column.format(value)
                            : value}
                          {column.id === "status" && row.onHoldDate ? <p className="p-0 m-0">{moment(row.onHoldDate).format("DD-MM-YYYY")}</p>  : ''}
                            
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              }
            })}
            
            {/* 
                  * Progress bar on loading APIs 
            */}
            
            <TableRow className={progressBar ? "" : "d-none"}>
              <TableCell colSpan={7} className="text-center">
                <CircularProgress disableShrink className="primary-loader" />
              </TableCell>
            </TableRow>

            {/* 
                * No data fount if there are no data array in API or clear btn clicked
            */}

            <TableRow className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "d-none"}>
              <TableCell
                colSpan={6}
                className="text-center progress-bar-center border-bottom-0"
              >
                <Label title="No data found" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
